import { Component } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RadioButtonModule } from "primeng/radiobutton";
import * as C from "../../../../../../shared/constants/globals.constants";
import { EWizDynamicBaseClassComponent } from "src/app/modules/esim/shared/classes/ewiz-dynamic.class";
import { ProgressCheck } from "src/app/shared/models/progress-check.model";
import * as ProfileModel from "src/app/shared/models/profile.model";

/** A selectable SUCI encryption scheme: a stored string value paired with its display label. */
interface EncryptionSchemeOption {
  readonly value: string;
  readonly label: string;
}

@Component({
  selector: C.EWIZ_SELECT_ENCRYPTION_SCHEME,
  templateUrl: "./select-encryption-scheme.component.html",
  standalone: true,
  imports: [FormsModule, RadioButtonModule]
})
export class EWizSelectEncryptionSchemeComponent extends EWizDynamicBaseClassComponent implements ProgressCheck {
  /** This dynamic wizard step is always rendered with a profile, so the inherited value is never null here. */
  declare profileValues: ProfileModel.ProfileDto;

  /** The encryption schemes offered for both the primary and secondary selections. */
  protected readonly encryptionSchemes: readonly EncryptionSchemeOption[] = [
    { value: "1", label: $localize`Null` },
    { value: "2", label: $localize`ECIES Profile A` },
    { value: "3", label: $localize`ECIES Profile B` }
  ];

  progressCheck(values: ProfileModel.ProfileDto) {
    if (values.suciEncryptionScheme.primaryEncryptionScheme === null) {
      return false;
    } else if (
      values.suciEncryptionScheme.addSecondEncryptionScheme === "1" &&
      values.suciEncryptionScheme.secondEncryptionScheme === null
    ) {
      return false;
    } else if (
      values.suciEncryptionScheme.addSecondEncryptionScheme === "1" &&
      values.suciEncryptionScheme.primaryEncryptionScheme === values.suciEncryptionScheme.secondEncryptionScheme
    ) {
      return false;
    } else if (values.suciEncryptionScheme.addSecondEncryptionScheme === null) {
      return false;
    } else if (
      values.suciEncryptionScheme.addSecondEncryptionScheme === "1" &&
      values.suciEncryptionScheme.secondEncryptionScheme !== null &&
      values.suciEncryptionScheme.addRemainingEncryptionScheme === null
    ) {
      return false;
    }

    return true;
  }

  /**
   * Determines whether a secondary encryption scheme option must be disabled because it
   * matches the already-selected primary scheme, preventing the same scheme being picked twice.
   */
  protected isSecondarySchemeDisabled(value: string): boolean {
    return this.profileValues?.suciEncryptionScheme.primaryEncryptionScheme === value;
  }

  /**
   * Clears a conflicting secondary scheme (and its dependent remaining-scheme answer) when the
   * primary scheme is changed to the value the secondary scheme already holds.
   */
  protected primaryEncryptionSchemeSelected(): void {
    const scheme = this.profileValues?.suciEncryptionScheme;

    if (scheme && scheme.secondEncryptionScheme === scheme.primaryEncryptionScheme) {
      scheme.secondEncryptionScheme = null;
      scheme.addRemainingEncryptionScheme = null;
    }

    this.handleFormEvents();
  }
}
